import { FC, useContext } from "react";

import DealBuilderContext from "~/contexts/DealBuilderContext";
import OrderContext from "~/contexts/OrderContext";
import TaxContext from "~/contexts/TaxContext";
import ThemeContext from "~/contexts/ThemeContext";
import {
  copyLine,
  findPartialDeal,
  withIndefiniteArticle,
} from "~/helpers/dealHelpers";
import { offerSavingBackground, offerSavingColour } from "~/helpers/offerTheme";
import SiteContext from "~/contexts/SiteContext";
import { formatReadyTime } from "~/helpers/dateHelper";

import styles from "./DealNotice.module.css";

// What happened to a deal in the last save: gained (auto-dismisses) or lost, with a way back
const DealNotice: FC = () => {
  const { dealNotice, dismissDealNotice, deals, openDealEditor } =
    useContext(DealBuilderContext);
  const {
    addOrderLines,
    removeOrderLines,
    readonlyCart,
    openOrderTimeModal,
    asapWait,
  } = useContext(OrderContext);
  const { site } = useContext(SiteContext);
  const { formatAmount } = useContext(TaxContext);
  const { template } = useContext(ThemeContext);

  // Announced politely whether or not a notice is showing, so screen readers hear changes
  if (!dealNotice) {
    return <section role="status" aria-live="polite" className={styles.live} />;
  }

  if (dealNotice.kind === "gained") {
    return (
      <section
        role="status"
        aria-live="polite"
        className={styles.notice}
        style={{
          backgroundColor: offerSavingBackground(template),
          color: offerSavingColour(template),
        }}
      >
        {dealNotice.name} added · you save {formatAmount(dealNotice.saving)}
      </section>
    );
  }

  if (dealNotice.kind === "freeAdded") {
    return (
      <section
        role="status"
        aria-live="polite"
        className={`${styles.notice} ${styles.withAction}`}
        style={{
          backgroundColor: offerSavingBackground(template),
          color: offerSavingColour(template),
        }}
      >
        <span>{dealNotice.name} added to your order</span>
        <button
          type="button"
          className={styles.action}
          disabled={readonlyCart}
          onClick={() => {
            // Takes the item out; the code stays on the order
            removeOrderLines(dealNotice.lineIds);
            dismissDealNotice();
          }}
        >
          Undo
        </button>
      </section>
    );
  }

  if (dealNotice.kind === "offerLost") {
    const reason =
      dealNotice.cause === "time"
        ? `Your order time is now ${formatReadyTime(
            dealNotice.requestedReadyTime,
            site.timeZone,
            asapWait,
            true,
          ).replace(/^Today, /, "today, ")}.`
        : dealNotice.cause === "orderType"
          ? "Your order type changed."
          : "Your order changed.";
    return (
      <section
        role="status"
        aria-live="polite"
        className={`${styles.notice} ${styles.info}`}
      >
        <b>
          {dealNotice.name}{" "}
          {dealNotice.cause === "time"
            ? "ended for this order"
            : "no longer applies"}
        </b>
        <span>
          {reason}
          {dealNotice.increase > 0 &&
            ` Your total went up ${formatAmount(dealNotice.increase)}.`}
        </span>
        {dealNotice.cause !== "cart" && (
          <span className={styles.actions}>
            <button
              type="button"
              className={styles.action}
              onClick={() => {
                openOrderTimeModal();
                dismissDealNotice();
              }}
            >
              {dealNotice.cause === "time" ? "Change time" : "Change order"}
            </button>
          </span>
        )}
      </section>
    );
  }

  const deal = deals.find((x) => x.code === dealNotice.promotionCode);
  const partial = deal
    ? findPartialDeal([deal], dealNotice.remainingLines)
    : null;

  return (
    <section
      role="status"
      aria-live="polite"
      className={`${styles.notice} ${styles.lost}`}
    >
      <b>{dealNotice.name} no longer applies</b>
      <span>{dealNotice.reason}</span>
      <span className={styles.actions}>
        {partial && (
          <button
            type="button"
            className={styles.action}
            disabled={readonlyCart}
            onClick={() => {
              openDealEditor(partial.deal.code, dealNotice.remainingLines);
              dismissDealNotice();
            }}
          >
            Add {withIndefiniteArticle(partial.slot.label.toLowerCase())}
            {partial.increase > 0 && ` · +${formatAmount(partial.increase)}`}
          </button>
        )}
        {!!dealNotice.removedLines.length && (
          <button
            type="button"
            className={styles.action}
            disabled={readonlyCart}
            onClick={() => {
              addOrderLines(dealNotice.removedLines.map(copyLine));
              dismissDealNotice();
            }}
          >
            Undo
          </button>
        )}
      </span>
    </section>
  );
};

export default DealNotice;
